import { useState, useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Search,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  XCircle,
  MinusCircle,
} from 'lucide-react';
import { useStore } from '../../stores/useStore';
import { highlightKeywords } from '../../lib/analyzer';
import type { Paper } from '../../types';
import clsx from 'clsx';

const COLORS = {
  inclusion: '#22c55e',
  exclusion: '#ef4444',
  both: '#f59e0b',
  none: '#94a3b8',
};

function PaperCard({ paper }: { paper: Paper }) {
  const { settings } = useStore();
  const [expanded, setExpanded] = useState(false);

  const { html: titleHtml } = settings.highlightKeywords
    ? highlightKeywords(
        paper.title,
        paper.matchedInclusionKeywords,
        paper.matchedExclusionKeywords,
        { caseSensitive: settings.caseSensitive }
      )
    : { html: paper.title };

  // Prepare abstract with highlighting
  const { html: abstractHtml } = paper.abstract && settings.highlightKeywords
    ? highlightKeywords(
        paper.abstract,
        paper.matchedInclusionKeywords,
        paper.matchedExclusionKeywords,
        { caseSensitive: settings.caseSensitive }
      )
    : { html: paper.abstract || '' };

  const categoryIcon = {
    inclusion: <CheckCircle2 className="w-4 h-4 text-accent-500" />,
    exclusion: <XCircle className="w-4 h-4 text-danger-500" />,
    both: <AlertCircle className="w-4 h-4 text-amber-500" />,
    none: <MinusCircle className="w-4 h-4 text-slate-400" />,
  };

  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {categoryIcon[paper.category || 'none']}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium"
              dangerouslySetInnerHTML={{ __html: titleHtml }}
            />
            <span className="flex-shrink-0 text-xs font-mono text-slate-400 dark:text-slate-500">
              {paper.pmid}
            </span>
          </div>

          {paper.abstract && (
            <p
              className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mt-2"
              dangerouslySetInnerHTML={{ __html: abstractHtml }}
            />
          )}

          {(paper.matchedInclusionKeywords.length > 0 ||
            paper.matchedExclusionKeywords.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {paper.matchedInclusionKeywords.map((kw, i) => (
                <span key={`inc-${i}`} className="badge-success text-xs">
                  + {kw}
                </span>
              ))}
              {paper.matchedExclusionKeywords.map((kw, i) => (
                <span key={`exc-${i}`} className="badge-danger text-xs">
                  - {kw}
                </span>
              ))}
            </div>
          )}

          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 space-y-1 text-sm text-slate-500">
              {paper.authors && <p><strong>Authors:</strong> {paper.authors}</p>}
              {paper.journal && <p><strong>Journal:</strong> {paper.journal}</p>}
              {paper.year && <p><strong>Year:</strong> {paper.year}</p>}
              {paper.doi && (
                <a
                  href={`https://doi.org/${paper.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                >
                  DOI <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3 h-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                Show more
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ResultsView() {
  const { results, statistics, filter, setFilter, setActiveTab } = useStore();
  const [activeCategory, setActiveCategory] = useState<'all' | 'inclusion' | 'exclusion' | 'both' | 'none'>('all');

  // Filter papers - must be called before any early returns (React hooks rule)
  const filteredPapers = useMemo(() => {
    if (!results) return [];

    let papers: Paper[] = [];

    if (activeCategory === 'all') {
      papers = [
        ...results.inclusionOnly,
        ...results.exclusionOnly,
        ...results.both,
        ...results.none,
      ];
    } else if (activeCategory === 'inclusion') {
      papers = results.inclusionOnly;
    } else if (activeCategory === 'exclusion') {
      papers = results.exclusionOnly;
    } else if (activeCategory === 'both') {
      papers = results.both;
    } else {
      papers = results.none;
    }

    // Apply search filter
    if (filter.searchQuery) {
      const query = filter.searchQuery.toLowerCase();
      papers = papers.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.pmid.toLowerCase().includes(query)
      );
    }

    return papers;
  }, [results, activeCategory, filter.searchQuery]);

  if (!results) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <AlertCircle className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
        <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
          No Results Yet
        </h3>
        <p className="text-slate-500 dark:text-slate-400 mb-4">
          Run an analysis to see results here
        </p>
        <button onClick={() => setActiveTab('keywords')} className="btn-primary">
          Go to Keywords
        </button>
      </div>
    );
  }

  // Prepare chart data
  const pieData = [
    { name: 'Inclusion Only', value: results.inclusionOnly.length, color: COLORS.inclusion },
    { name: 'Exclusion Only', value: results.exclusionOnly.length, color: COLORS.exclusion },
    { name: 'Both', value: results.both.length, color: COLORS.both },
    { name: 'None', value: results.none.length, color: COLORS.none },
  ].filter((d) => d.value > 0);

  const keywordBarData = statistics?.keywordStats.slice(0, 10).map((stat) => ({
    name: stat.keyword.length > 15 ? stat.keyword.substring(0, 15) + '...' : stat.keyword,
    count: stat.count,
    fill: stat.type === 'inclusion' ? COLORS.inclusion : COLORS.exclusion,
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Analysis Results
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Processed {results.total.toLocaleString()} papers in{' '}
          {(results.processingTime / 1000).toFixed(2)}s
        </p>
      </div>

      {/* Statistics cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { key: 'inclusion', label: 'Inclusion Only', count: results.inclusionOnly.length, color: 'accent' },
          { key: 'exclusion', label: 'Exclusion Only', count: results.exclusionOnly.length, color: 'danger' },
          { key: 'both', label: 'Both', count: results.both.length, color: 'warning' },
          { key: 'none', label: 'None', count: results.none.length, color: 'neutral' },
        ].map((stat) => (
          <button
            key={stat.key}
            onClick={() => setActiveCategory(stat.key as any)}
            className={clsx(
              'card p-4 text-left transition-all duration-200',
              activeCategory === stat.key
                ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-slate-900'
                : 'hover:shadow-md'
            )}
          >
            <div className="text-3xl font-bold text-slate-900 dark:text-white">
              {stat.count.toLocaleString()}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {stat.label}
            </div>
            <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              {((stat.count / results.total) * 100).toFixed(1)}%
            </div>
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart - top keywords */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Top Keywords
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={keywordBarData} layout="vertical">
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {keywordBarData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Papers list */}
      <div className="card">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900 dark:text-white">
                Papers
              </h3>
              <span className="badge-neutral">
                {filteredPapers.length.toLocaleString()}
              </span>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={filter.searchQuery}
                  onChange={(e) => setFilter({ searchQuery: e.target.value })}
                  placeholder="Search papers..."
                  className="input pl-9"
                />
              </div>

              <select
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value as any)}
                className="input w-auto"
              >
                <option value="all">All Categories</option>
                <option value="inclusion">Inclusion Only</option>
                <option value="exclusion">Exclusion Only</option>
                <option value="both">Both</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4 max-h-[600px] overflow-y-auto scrollbar-thin space-y-3">
          {filteredPapers.length > 0 ? (
            filteredPapers.slice(0, 100).map((paper) => (
              <PaperCard key={paper.id} paper={paper} />
            ))
          ) : (
            <div className="text-center py-12 text-slate-400">
              No papers match your criteria
            </div>
          )}

          {filteredPapers.length > 100 && (
            <p className="text-center text-sm text-slate-400 pt-4">
              Showing first 100 of {filteredPapers.length.toLocaleString()} papers
            </p>
          )}
        </div>
      </div>

      {/* Continue to export */}
      <div className="flex justify-end">
        <button onClick={() => setActiveTab('export')} className="btn-primary">
          Continue to Export
          <span className="ml-1">→</span>
        </button>
      </div>
    </div>
  );
}
